"use client"

import { getCatalogImageUrls, handleCopyLink as copyLink, handleDownload as downloadLeaflet } from "@/components/leaflet/utils"
import { LeafletSidebar } from "@/components/leaflet/LeafletSidebar"
import { t } from "@/languages/i18n"
import { LetakResponseDataObject } from "@/types/codegen/data-contracts"
import dynamic from "next/dynamic"
import { useEffect, useState } from "react"
import { LeafletMobileHeader } from "./components/LeafletMobileHeader"
import { LeafletRelatedItems } from "./components/LeafletRelatedItems"

// react-pageflip thao tác DOM trực tiếp; LeafletShare đọc window.location khi render
// → chỉ render ở client để tránh lỗi SSR / lệch hydration.
const LeafletFlipbook = dynamic(() => import("./components/LeafletFlipbook").then(m => m.LeafletFlipbook), {
    ssr: false,
})
const LeafletShare = dynamic(() => import("@/components/leaflet/LeafletShare").then(m => m.LeafletShare), {
    ssr: false,
})

interface LeafletDetailViewProps {
    /** Dữ liệu đã được server (page.tsx) đọc theo Draft Mode — bản nháp chỉ tới đây khi đang xem trước. */
    leaflet: LetakResponseDataObject
    relatedLeaflets: LetakResponseDataObject[]
    locale: string
}

export default function LeafletDetailView({ leaflet, relatedLeaflets, locale }: LeafletDetailViewProps) {
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const id = leaflet.id as number

    const handleFullscreen = () => {
        const container = document.getElementById('leaflet-container')
        if (!container) return

        if (!document.fullscreenElement) {
            container.requestFullscreen().then(() => {
                setIsFullscreen(true)
                document.body.style.overflow = 'hidden'
            }).catch((err) => {
                console.error("Error attempting to enable fullscreen:", err)
            })
        } else {
            document.exitFullscreen().then(() => {
                setIsFullscreen(false)
                document.body.style.overflow = ''
            }).catch((err) => {
                console.error("Error attempting to exit fullscreen:", err)
            })
        }
    }

    const handleDownload = () => {
        downloadLeaflet(leaflet)
    }

    const handleCopyLink = () => {
        copyLink(leaflet, locale, () => {
            alert(t("Copied to clipboard"))
        }, (err) => {
            console.error(t("Failed to copy link"), err)
        })
    }

    // Listen to fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFull = !!document.fullscreenElement
            setIsFullscreen(isFull)

            if (isFull) {
                document.body.style.overflow = 'hidden'
                const header = document.querySelector('header')
                const footer = document.querySelector('footer')
                if (header) (header as HTMLElement).style.display = 'none'
                if (footer) (footer as HTMLElement).style.display = 'none'
            } else {
                document.body.style.overflow = ''
                const header = document.querySelector('header')
                const footer = document.querySelector('footer')
                if (header) (header as HTMLElement).style.display = ''
                if (footer) (footer as HTMLElement).style.display = ''
            }
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
        }
    }, [])

    const catalogImages = getCatalogImageUrls(leaflet)

    return (
        <>
            <div
                id="leaflet-container"
                className={`sm:min-h-screen bg-[#f3f4f8] flex sm:absolute top-0 left-0 w-full sm:z-[9999] z-[49] ${isFullscreen ? 'fixed inset-0 z-[9999]' : ''}`}
                // Khung phủ toàn trang (z 9999) sẽ đè admin-bar khi xem trước → dời xuống
                // đúng chiều cao bar (`--admin-bar-h`, mặc định 0) như header của site.
                style={isFullscreen ? undefined : { top: "var(--admin-bar-h, 0px)" }}
            >
                <LeafletSidebar
                    locale={locale}
                    isFullscreen={isFullscreen}
                    showShareModal={showShareModal}
                    currentItem={leaflet}
                    onFullscreen={handleFullscreen}
                    onDownload={handleDownload}
                    onShare={() => setShowShareModal(!showShareModal)}
                    variant="detail"
                />

                <main className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden sm:p-0 px-2 py-5">
                    <LeafletMobileHeader leaflet={leaflet} />

                    {catalogImages.length > 0 ? (
                        <LeafletFlipbook images={catalogImages} />
                    ) : (
                        <div className="flex items-center justify-center h-full flex-col gap-4">
                            <p className="text-gray-500">{t("No detailed content")}</p>
                        </div>
                    )}
                </main>

                {!isFullscreen && (
                    <LeafletRelatedItems
                        items={relatedLeaflets}
                        locale={locale}
                    />
                )}

                <LeafletShare
                    item={leaflet}
                    id={id}
                    locale={locale}
                    onCopyLink={handleCopyLink}
                    variant="panel"
                    show={showShareModal}
                />
            </div>
        </>
    )
}
